<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, ref, useTemplateRef } from "vue";
import { locale } from "../locale";
import { authState, commitAuthState } from "../states/auth";
import BaseButton from "./BaseButton.vue";
import DeviceComboBox from "./DeviceComboBox.vue";
import ExitModalBtn from "./ExitModalBtn.vue";
import InputBox from "./InputBox.vue";
import { isActiveRoom } from "../states/useActiveRoomState";
import {
  combineValidators,
  getEmptyFieldValidator,
  getMaxLenValidator,
} from "../utils/validators";
import PreviewVideoCardNew from "./PreviewVideoCardNew.vue";
import BackgroundEffects from "./BackgroundEffects.vue";
import MicroSensitivityControl from "./MicroSensitivityControl.vue";
import { desiredMediaState } from "../states/media/state";
import type { BackgroundEffect } from "../states/media/state";
import {
  noiseSuppression,
  currentMicroLevel,
  setNoiseSuppression,
} from "../states/media/audioProcessor";
import { mediaActions } from "../states/media/actions";
import {
  audioDevices,
  audioDeviceSelectedVisible,
  cameraDevices,
  cameraDeviceSelectedVisible,
  microDevices,
  microDeviceSelectedVisible,
} from "../states/media/devices";
import { closeSettingModal } from "../states/viewState";
import SimpleBar from "simplebar";

const savedValues = {
  name: authState.displayedName,
  micro: desiredMediaState.microDevice,
  audio: desiredMediaState.audioDevice,
  camera: desiredMediaState.cameraDevice,
  backgroundEffect: {
    ...desiredMediaState.backgroundEffect,
  } as BackgroundEffect,
  noiseSuppression: noiseSuppression.value,
};

const nameValidator = combineValidators([
  getEmptyFieldValidator(locale.validators.fieldnames.name).validator,
  getMaxLenValidator(600, locale.validators.fieldnames.name).validator,
]);
const mode = computed(() => {
  if (isActiveRoom.value === true) {
    return "black";
  } else return "white";
});
provide("colormode", mode);
const input = ref<typeof InputBox | null>(null);
const apply = async () => {
  savedValues.micro = desiredMediaState.microDevice;
  savedValues.camera = desiredMediaState.cameraDevice;
  savedValues.audio = desiredMediaState.audioDevice;
  savedValues.backgroundEffect = { ...desiredMediaState.backgroundEffect };
  if (mode.value === "black") {
    authState.displayedName = authState.displayedName.trim();
    await new Promise((resolve) => {
      nextTick(() => resolve(null));
    });
    nameValidator.validator(authState.displayedName);
    if (!nameValidator.state.value) {
      input.value?.shake();
      return;
    }
    await commitAuthState();
  }
  closeSettingModal();
};

const cancel = () => {
  if (mode.value === "black") {
    authState.displayedName = savedValues.name;
  }
  if (
    savedValues.micro !== desiredMediaState.microDevice &&
    savedValues.micro
  ) {
    mediaActions.setMicroDevice(savedValues.micro);
  }
  if (
    savedValues.camera !== desiredMediaState.cameraDevice &&
    savedValues.camera
  ) {
    mediaActions.setCameraDevice(savedValues.camera);
  }
  if (
    savedValues.audio !== desiredMediaState.audioDevice &&
    savedValues.audio
  ) {
    mediaActions.setAudioDevice(savedValues.audio);
  }
  if (
    JSON.stringify(desiredMediaState.backgroundEffect) !==
    JSON.stringify(savedValues.backgroundEffect)
  ) {
    mediaActions.setBackgroundEffect(savedValues.backgroundEffect);
  }
  if (noiseSuppression.value !== savedValues.noiseSuppression) {
    setNoiseSuppression(savedValues.noiseSuppression);
  }
  closeSettingModal();
};

function updateNoiseSuppression(value: boolean) {
  setNoiseSuppression(value);
}

const scrollEl = useTemplateRef('scrollEl');
onMounted(() => {
  mediaActions.requestCameraOnSettings(true);
  if (scrollEl.value) {
    new SimpleBar(scrollEl.value, { autoHide: false });
  }

});
onUnmounted(() => {
  mediaActions.requestCameraOnSettings(false);
});
const audioVisibleText = audioDeviceSelectedVisible();
const microVisibleText = microDeviceSelectedVisible();
const cameraVisibleText = cameraDeviceSelectedVisible();
</script>
<template>
  <div :class="`modal-area ${mode}`">
    <div class="block-wrapper">
      <div :class="`setting-modal ${mode}`">
        <div class="title">
          <span class="text">{{ locale.settings }}</span>
          <ExitModalBtn @click="cancel" />
        </div>
        <div ref="scrollEl" class="scroll-wrapper">
          <div class="scroll-content">
            <InputBox
              v-if="mode === 'black'"
              ref="input"
              v-model="authState.displayedName"
              :title="locale.youname"
              :validator="nameValidator.validator"
              :placeholder="locale.required"
            />
            <DeviceComboBox
              :title="locale.sound"
              :visible-text="audioVisibleText"
              :list="audioDevices"
              :disabled="audioDevices.length === 0"
              @select="(val) => mediaActions.setAudioDevice(val)"
            />
            <DeviceComboBox
              :title="locale.micro"
              :visible-text="microVisibleText"
              :list="microDevices"
              :disabled="microDevices.length === 0"
              @select="(val) => mediaActions.setMicroDevice(val)"
            />
            <div class="micro-sensitivity-control-wrapper">
              <MicroSensitivityControl
                :current-micro-level="currentMicroLevel"
                :noise-suppression="noiseSuppression"
                @update-noise-suppression="updateNoiseSuppression"
              />
            </div>
            <div class="blur-settings">
              <div class="preview-video">
                <PreviewVideoCardNew />
              </div>
              <div class="camera-controls">
                <DeviceComboBox
                  :title="locale.webcam"
                  :visible-text="cameraVisibleText"
                  :list="cameraDevices"
                  class="camera-selector"
                  :disabled="cameraDevices.length === 0"
                  no-padding
                  @select="(val) => mediaActions.setCameraDevice(val)"
                />
              </div>
            </div>
            <BackgroundEffects />
          </div>
        </div>
        <div class="controls">
          <BaseButton
            color="green"
            @click="apply"
          >
            {{ locale.apply }}
          </BaseButton>
          <BaseButton
            color="grey"
            @click="cancel"
          >
            {{ locale.cancel }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.scroll-wrapper {
  max-height: calc(100vh - 204px);
}

.scroll-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px;
}

.modal-area {
  display: flex;
  /* justify-content: center; */
  /* align-items: center; */
  position: absolute;
  overflow: auto;
  left: 0;
  top: 0;
  height: 100%;
  height: 100dvh;
  width: 100vw;
  z-index: 100;
}

.block-wrapper {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: max-content;
  min-width: max-content;
}

.micro-sensitivity-control-wrapper {
  padding: 0 12px;
}

.modal-area.black {
  background: rgba(41, 41, 41, 0.7);
}

.setting-modal {
  width: 600px;
  border-radius: 10px;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
  padding: 20px 0 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.setting-modal.black {
  /* контент 600px, как у .white: 604 = 600 + рамка 2×2px (border-box) */
  width: 604px;
  border: 2px solid #494949;
  background: #292929;
}

.setting-modal.white {
  background-color: #fff;
}

.title {
  height: 24px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.black .title .text {
  color: #dbdbdb;
}

.white .title .text {
  color: #292929;
}

.title .text {
  text-align: center;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
}

.controls {
  height: 60px;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  padding: 12px 24px;
  flex-direction: row-reverse;
  gap: 24px;
  justify-content: space-between;
}

.blur-settings {
  display: flex;
  gap: 12px;
  padding: 12px 12px 4px 12px;
}

.camera-controls {
  display: flex;
  flex-direction: column;
  width: 200px;
  flex-grow: 1;
}

.camera-selector {
  margin-bottom: 3px;
}

.setting-modal :deep(.simplebar-vertical .simplebar-scrollbar::before) {
  width: 5px;
  background: #A4A4A4;
}

.setting-modal :deep(.simplebar-vertical) {
  top: 4px !important;
  bottom: 4px !important;
}
</style>
